import TokenResponse from 'owa-service/lib/contract/TokenResponse';
import tokenRequest from 'owa-service/lib/factory/tokenRequest';
import RequestOptions from 'owa-service/lib/RequestOptions';
import sleep from 'owa-sleep';
import addSeconds from 'owa-date-utc-fn/lib/addSeconds';
import getAccessTokenforResourceOperation from 'owa-service/lib/operation/getAccessTokenforResourceOperation';
import { getUserToken } from './utils/userTokenHandler';
import { getOrigin } from 'owa-url/lib/getOrigin';
import TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import { getOpxHostApi } from 'owa-opx';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

export const SCENARIO_NAME = 'OWA_GetAccessTokenForResource';

export default async function getAccessTokenforResourceFromServerOrHostApp(
    resource: string,
    retryCount: number,
    requestId: string,
    apiName?: string,
    targetTenantId?: string,
    wwwAuthenticateHeader?: string
): Promise<TokenResponse> {
    const userToken = await getUserToken();
    if (userToken && resource == getOrigin()) {
        return { AccessToken: userToken };
    }

    if (isHostAppFeatureEnabled('opxComponentLifecycle')) {
        const opxHostApi = getOpxHostApi();
        if (
            opxHostApi.isResourceTokenPrefetchEnabled &&
            (await opxHostApi.isResourceTokenPrefetchEnabled())
        ) {
            // to do: handling the consumer scenarios when token is empty, since fallback mechanism will not work here
            const resourceToken =
                opxHostApi.getResourceToken &&
                (await opxHostApi.getResourceToken(resource, wwwAuthenticateHeader));
            if (
                resourceToken &&
                resourceToken.length > 0 &&
                resourceToken.split(' ')[0]?.toLowerCase() === 'bearer' && // check if the token has bearer prefix
                resourceToken.split(' ')[1]?.length > 0
            ) {
                return {
                    AccessToken: resourceToken.split(' ')[1],
                    TokenResultCode: TokenResponseCode.Success,
                    ExpiresIn: 3600, // value in seconds
                    AccessTokenExpiry: addSeconds(Date.now(), 3600).toISOString(), // keeping the token validity for 1 hour
                };
            }
        }
    }

    const options: RequestOptions = {
        isUserActivity: false,
        datapoint: {
            customData: {
                Scenario: SCENARIO_NAME,
                Resource_Url: resource,
                Retry_attempt: retryCount,
                Request_Id: requestId,
                Target_Tenant_Id: targetTenantId || '',
                Api: apiName || '',
            },
            headersCustomData: (headers: Headers) => {
                return {
                    TokenResultDetails: headers.get('x-tokenresult') || '',
                };
            },
            jsonCustomData: (tokenResponse: TokenResponse) => {
                return {
                    TokenStatus:
                        tokenResponse?.TokenResultCode?.toString() || TokenResponseCode.Failed,
                };
            },
        },
        returnFullResponseOnSuccess: true,
    };

    const response = await (getAccessTokenforResourceOperation(
        tokenRequest({ Resource: resource, UserToken: userToken, TargetTenantId: targetTenantId }),
        options
    ) as Promise<Response>);

    return (await response.json()) as TokenResponse;
}

/**
 * Adds a retry with timeout when fetching Access Token from server
 * @param resource - the resource string
 * @param retryCount - the retry count
 * @param retryDelay - time between 2 retries
 * @param requestId - the requestId
 * @param apiName - name of the api
 * @returns - TokenResponse wrapped around in a Promise
 */
export async function getAccessTokenforResourceFromServerOrHostAppWithRetry(
    resource: string,
    retryCount: number,
    retryDelay: number,
    requestId: string,
    apiName?: string,
    targetTenantId?: string,
    wwwAuthenticateHeader?: string
): Promise<TokenResponse> {
    if (retryCount < 1) {
        return Promise.reject();
    }

    return getAccessTokenforResourceFromServerOrHostApp(
        resource,
        retryCount,
        requestId,
        apiName,
        targetTenantId,
        wwwAuthenticateHeader
    ).catch(error =>
        sleep(retryDelay).then(() => {
            return getAccessTokenforResourceFromServerOrHostAppWithRetry(
                resource,
                retryCount - 1,
                retryDelay * 2,
                requestId,
                apiName,
                targetTenantId,
                wwwAuthenticateHeader
            );
        })
    );
}
